import React, { useState, useEffect } from 'react';
import { integrationService } from '../services/integrationService';

const INTEGRATION_NAMES = {
  ismartrecruit: 'iSmartRecruit',
  bullhorn: 'Bullhorn',
  recruitcrm: 'Recruit CRM'
};

export default function IntegrationLogs({ integrationType, onCancel }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sampleData, setSampleData] = useState({});
  const [loadingSamples, setLoadingSamples] = useState(new Set());
  const [cancelling, setCancelling] = useState(new Set());
  const pollingIntervalRef = React.useRef(null);

  useEffect(() => {
    loadLogs();
  }, [integrationType]);

  useEffect(() => {
    // Start polling for updates every 5 seconds if there are active syncs
    const checkAndPoll = () => {
      const hasActiveSyncs = logs.some(log => 
        log.status && ['pending', 'running'].includes(log.status.toLowerCase())
      );
      if (hasActiveSyncs) {
        loadLogs();
      }
    };
    
    pollingIntervalRef.current = setInterval(checkAndPoll, 5000);
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [logs]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await integrationService.getSyncLogs(integrationType, 50);
      
      if (Array.isArray(data)) {
        setLogs(data);
      } else {
        console.warn('Expected array but got:', typeof data, data);
        setLogs([]);
      }
    } catch (err) {
      console.error('Failed to load sync logs:', err);
      setError(err.message || 'Failed to load sync logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (syncRunId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(syncRunId)) {
      newExpanded.delete(syncRunId);
    } else {
      newExpanded.add(syncRunId);
    }
    setExpandedRows(newExpanded);
  };

  const loadSampleData = async (syncRunId, objectType) => {
    const key = `${syncRunId}_${objectType}`;
    if (sampleData[key]) {
      return; // Already loaded
    }

    setLoadingSamples(prev => new Set(prev).add(key));
    try {
      const data = await integrationService.getSnapshotSample(
        integrationType,
        syncRunId,
        objectType,
        5
      );
      setSampleData(prev => ({ ...prev, [key]: data }));
    } catch (err) {
      console.error(`Failed to load sample data for ${objectType}:`, err);
      setSampleData(prev => ({
        ...prev,
        [key]: { error: err.message || 'Failed to load sample data' }
      }));
    } finally {
      setLoadingSamples(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRecordCounts = (recordCounts) => {
    if (!recordCounts || typeof recordCounts !== 'object') return 'N/A';
    const entries = Object.entries(recordCounts)
      .filter(([_, count]) => count > 0)
      .map(([key, value]) => `${key}: ${value}`);
    return entries.length > 0 ? entries.join(', ') : 'No records';
  };

  const handleCancelSync = async (syncRunId) => {
    if (!window.confirm('Are you sure you want to cancel this sync job?')) {
      return;
    }

    try {
      setCancelling(prev => new Set(prev).add(syncRunId));
      await integrationService.cancelSync(integrationType, syncRunId);
      // Reload logs to reflect the cancellation
      await loadLogs();
    } catch (err) {
      alert(`Failed to cancel sync: ${err.message}`);
    } finally {
      setCancelling(prev => {
        const newSet = new Set(prev);
        newSet.delete(syncRunId);
        return newSet;
      });
    }
  };

  const isActiveSync = (status) => {
    return status && ['pending', 'running'].includes(status.toLowerCase());
  };

  const renderSampleData = (syncRunId, objectType) => {
    const key = `${syncRunId}_${objectType}`;
    const data = sampleData[key];
    const isLoading = loadingSamples.has(key);

    if (isLoading) {
      return (
        <div className="p-4 text-center">
          <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-[#00C6AE]"></div>
          <span className="ml-2 text-sm text-gray-500">Loading sample...</span>
        </div>
      );
    }

    if (!data) {
      return (
        <button
          onClick={() => loadSampleData(syncRunId, objectType)}
          className="text-sm text-[#00C6AE] hover:text-[#00A693] transition-colors"
        >
          Load sample data →
        </button>
      );
    }

    if (data.error) {
      return <span className="text-sm text-red-600">{data.error}</span>;
    }

    if (!data.sample_records || data.sample_records.length === 0) {
      return <span className="text-sm text-gray-500">No sample data available</span>;
    }

    return (
      <div className="mt-2">
        <div className="text-xs text-gray-500 mb-2">
          Showing {data.sample_size} of {data.total_count} {objectType}
        </div>
        <div className="bg-gray-50 rounded-lg p-3 max-h-64 overflow-y-auto">
          {data.sample_records.map((record, idx) => (
            <div key={idx} className="mb-2 p-2 bg-white rounded border border-gray-200">
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(record, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
          <p className="mt-2 text-gray-500">Loading sync logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          Sync Logs - {INTEGRATION_NAMES[integrationType] || integrationType}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          ← Back to Integrations
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm font-medium">Error loading logs</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={loadLogs}
            className="mt-2 text-sm text-red-700 hover:text-red-900 underline"
          >
            Try again
          </button>
        </div>
      )}

      {!error && logs.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No sync runs found.</p>
          <p className="text-sm text-gray-400 mt-2">
            Trigger a sync to see logs here.
          </p>
        </div>
      )}

      {!error && logs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  {/* Expand column */}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Records
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Error
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => {
                const isExpanded = expandedRows.has(log.sync_run_id);
                const hasRecords = log.record_counts && 
                  Object.keys(log.record_counts).length > 0 && 
                  Object.values(log.record_counts).some(count => count > 0);
                const isCompleted = (log.status || '').toLowerCase() === 'completed';

                return (
                  <React.Fragment key={log.sync_run_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {hasRecords && isCompleted && (
                          <button
                            onClick={() => toggleRow(log.sync_run_id)}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                          {log.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(log.started_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(log.completed_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.business_date ? formatDate(log.business_date) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatRecordCounts(log.record_counts)}
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600 max-w-xs truncate" title={log.error_message || ''}>
                        {log.error_message || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isActiveSync(log.status) && (
                          <button
                            onClick={() => handleCancelSync(log.sync_run_id)}
                            disabled={cancelling.has(log.sync_run_id)}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                              cancelling.has(log.sync_run_id)
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {cancelling.has(log.sync_run_id) ? 'Cancelling...' : 'Cancel'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && hasRecords && isCompleted && (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-4">
                            <h4 className="font-semibold text-gray-900">Sample Data</h4>
                            {Object.entries(log.record_counts)
                              .filter(([_, count]) => count > 0)
                              .map(([objectType, count]) => (
                                <div key={objectType} className="border-l-4 border-[#00C6AE] pl-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-medium text-gray-800 capitalize">
                                      {objectType} ({count} records)
                                    </h5>
                                  </div>
                                  {renderSampleData(log.sync_run_id, objectType)}
                                </div>
                              ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button
          onClick={loadLogs}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
